import React, { useEffect, useLayoutEffect, useCallback, useRef } from "react";

import "./PrivacyPolicy.scss";
import { useSelector } from "react-redux";
import { ApplicationState } from "../../../store";
import { LayoutState } from "../../../store/reducers/layout-reducer";
import { PrivacyState } from "../../../store/reducers/privacy-reducer";
import FocusTrapping from "../../../components/__hoc/FocusTrapping";
import { Container } from "../../../components/Grid/Grid";
import { IFRAME_ID } from "../../../constants/privacyPolicyConstants";
import { usePrivacyPolicy } from "../../../hooks/usePrivacyPolicy";
import { usePrivacyPolicyIframe } from "../../../hooks/usePrivacyPolicyIframe";
import { usePrivacyPolicyVersionHistory } from "../../../hooks/usePrivacyPolicyVersionHistory";
import {
	parseLanguageString,
	applyIframeTheme,
} from "../../../utils/privacyPolicyUtils";
import { PrivacyPolicyHeader } from "./components/PrivacyPolicyHeader";
import { PrivacyPolicySkeleton } from "./components/PrivacyPolicySkeleton";
import { LanguageModal } from "../../../components/LanguageModal/LanguageModal";
import { VersionHistoryModal } from "../../../components/VersionHistoryModal/VersionHistoryModal";

export const PrivacyPolicy: React.FC = () => {
	// Get layout and privacy state from Redux
	const layoutState = useSelector<ApplicationState, LayoutState>(
		(state) => state.layout
	);
	const privacyState = useSelector<ApplicationState, PrivacyState>(
		(state) => state.privacy
	);

	// Refs
	const topRef = useRef<HTMLDivElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);

	// Privacy policy hook (LPP detection + iframe handlers + resize handling)
	const { hasLPP } = usePrivacyPolicy(layoutState.isDarkMode, containerRef);

	// Version history management hook
	const {
		versionHistories,
		hasVersionHistory,
		selectedHistory,
		ppSource_Global,
		ppSource_Local,
		handleSelectHistory,
		loadPrivacyPolicyVersionHistory,
	} = usePrivacyPolicyVersionHistory(privacyState.language, hasLPP);

	// Iframe management hook
	const {
		shouldRenderIframes,
		iframesReady,
		iframeGeneration,
		handleIframeLoad,
		triggerIframeReflow,
		resetIframeLoadStates,
	} = usePrivacyPolicyIframe(layoutState.isDarkMode, hasLPP);

	// Modal state
	const [isLanguageOpen, setLanguageOpen] = React.useState(false);
	const [isHistoryOpen, setHistoryOpen] = React.useState(false);

	// Parse language
	const { language } = privacyState;
	const [langCode, countryCode] = parseLanguageString(language);

	// Track previous language to detect changes (not initial mount)
	const prevLanguageRef = useRef<string>(language);

	// Close handlers with useCallback for performance
	const handleCloseVersionHistory = useCallback(() => {
		setHistoryOpen(false);
		document.getElementById("version-history-button")?.focus();
	}, []);

	const handleClosePolicyLanguage = useCallback(() => {
		setLanguageOpen(false);
		document.getElementById("policy-language-button")?.focus();
	}, []);

	// Open handlers with useCallback for performance
	const handleOpenVersionHistory = useCallback(() => setHistoryOpen(true), []);
	const handleOpenPolicyLanguage = useCallback(() => setLanguageOpen(true), []);

	// Handle version history selection and close modal
	const handleVersionHistorySelect = useCallback(
		async (history: Parameters<typeof handleSelectHistory>[0]) => {
			await handleSelectHistory(history);
			setHistoryOpen(false);
			document.getElementById("version-history-button")?.focus();
		},
		[handleSelectHistory]
	);

	// Load version history on language change
	useEffect(() => {
		if (language === "") return;
		loadPrivacyPolicyVersionHistory(countryCode, langCode);
	}, [language, countryCode, langCode, loadPrivacyPolicyVersionHistory]);

	// Reset iframe load states when content source changes (language change)
	useEffect(() => {
		resetIframeLoadStates();
	}, [ppSource_Global, ppSource_Local, resetIframeLoadStates]);

	// Synchronously trigger iframe rendering before paint to prevent layout shift
	useLayoutEffect(() => {
		triggerIframeReflow();
		topRef.current?.focus();
	}, [triggerIframeReflow]);

	// Apply theme changes to already-loaded iframes when dark mode toggles
	useEffect(() => {
		const globalIframe = document.getElementById(
			IFRAME_ID
		) as HTMLIFrameElement | null;
		if (globalIframe?.contentDocument) {
			applyIframeTheme(globalIframe.contentDocument, layoutState.isDarkMode);
		}

		const localIframe = document.getElementById(
			`${IFRAME_ID}-local`
		) as HTMLIFrameElement | null;
		if (localIframe?.contentDocument) {
			applyIframeTheme(localIframe.contentDocument, layoutState.isDarkMode);
		}
	}, [layoutState.isDarkMode]);

	// Focus management for modals
	useEffect(() => {
		if (isLanguageOpen) {
			document.getElementById("language-modal-tts")?.focus();
		} else if (isHistoryOpen) {
			document.getElementById("version-modal-tts")?.focus();
		}
	}, [isLanguageOpen, isHistoryOpen]);

	// Scroll to top when the privacy policy language changes.
	// When the user changes language via the footer, the iframe reloads with new
	// content that may be shorter, causing the browser to clamp scrollTop upward.
	// Scrolling to the top provides a consistent UX on language change.
	useEffect(() => {
		if (language === "" || prevLanguageRef.current === language)
			return () => {};
		prevLanguageRef.current = language;

		const mainBody = document.getElementById("main-body");
		if (mainBody) {
			// Give iframe time to reflow (matches the 0ms timeout pattern above)
			const scrollTimer = window.requestAnimationFrame(() => {
				mainBody.scrollTo({ top: 0 });
			});
			return () => window.cancelAnimationFrame(scrollTimer);
		}
	}, [language]);

	return (
		<div className="privacy-policy">
			{/* Header Section */}
			<div ref={topRef} tabIndex={-1}>
				{/* Version History Modal */}
				<FocusTrapping
					onHandleClose={handleCloseVersionHistory}
					isActive={isHistoryOpen}
					modalId="version-history-modal"
				>
					<VersionHistoryModal
						headertestid="onhide-history-modal"
						selectedHistory={selectedHistory}
						show={isHistoryOpen}
						onHide={handleCloseVersionHistory}
						versionHistories={versionHistories}
						onHistorySelect={handleVersionHistorySelect}
					/>
				</FocusTrapping>
				<PrivacyPolicyHeader
					hasVersionHistory={hasVersionHistory}
					ppSource={ppSource_Global}
					ppSourceLocal={ppSource_Local}
					hasLPP={hasLPP}
					langCode={langCode}
					countryCode={countryCode}
					onVersionHistoryClick={handleOpenVersionHistory}
					onLanguageClick={handleOpenPolicyLanguage}
				/>
			</div>

			{/* Language Modal */}
			<FocusTrapping
				onHandleClose={handleClosePolicyLanguage}
				isActive={isLanguageOpen}
				modalId="language-modal-privacy-policy"
			>
				<LanguageModal
					headertestid="onhide-language-modal"
					id="language-modal-privacy-policy"
					key={1}
					show={isLanguageOpen}
					isSiteLanguage={false}
					onHide={handleClosePolicyLanguage}
				/>
			</FocusTrapping>

			{/* Global Policy Iframe Container */}
			{shouldRenderIframes && (
				<Container>
					{/* Skeleton Loader */}
					<PrivacyPolicySkeleton isVisible={!iframesReady} />

					{/* Iframe Container */}
					<div
						ref={containerRef}
						className={iframesReady ? "privacy-policy__iframe-container" : ""}
						style={{ display: iframesReady ? "block" : "none" }}
					>
						<iframe
							className={"mt-top-62 mb-bottom-48"}
							id={IFRAME_ID}
							src={ppSource_Global}
							width="100%"
							title="global-privacy-policy"
							onLoad={() => handleIframeLoad(IFRAME_ID, iframeGeneration)}
						/>

						{/* Local Policy Iframe (for LPP countries) */}
						{hasLPP && (
							<iframe
								id={`${IFRAME_ID}-local`}
								src={ppSource_Local}
								width="100%"
								title="local-privacy-policy"
								onLoad={() =>
									handleIframeLoad(`${IFRAME_ID}-local`, iframeGeneration)
								}
							/>
						)}
					</div>
				</Container>
			)}
		</div>
	);
};
